import { useEffect, useState, useMemo } from 'react';
import { IDriverUser } from '../../types/type';
import Spiner from '../Spiner/Spiner';
import './driverMedList.css';
import { MedicalService, MedListFilters } from '../../services/medical.service';
import { normalizeImageUrl, fullName } from '../../utils/utils';
import { formatBackendShortDate, formatBackendTime } from '../../utils/date.utils';
import Pagination from '../Pagination/Pagination';
import Table, {
  LabeledCell,
  LabeledSecondaryCell,
  ColumnConfig,
  ExpandedRowConfig,
  SecondaryRowConfig,
} from '../Table/Table';
import StatusCell from '../Table/StatusCell';
import DateRangeFilter from '../DateRangeFilter/DateRangeFilter';
import { useFilters, PaginatedFilters } from '../../hooks/useFilters';

interface IProps {
  driver: IDriverUser;
}

// Расширяем интерфейс MedListFilters, чтобы он соответствовал PaginatedFilters
interface ExtendedMedListFilters extends PaginatedFilters {
  // Дополнительные поля, если они есть в MedListFilters
}

export default function DriverMedList({ driver }: IProps) {
  const [medLists, setMedLists] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { filters, handleFilterChange, resetFilters, handlePageChange } = useFilters<ExtendedMedListFilters>({
    page: 1,
    limit: 10,
    startTime: '',
    endTime: '',
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
  });

  // Статус-маппинг для визуализации статусов
  const statusMap = {
    completed: { label: 'Допущен', dotClass: 'active' },
    rejected: { label: 'Не допущен', dotClass: 'rejected' },
    waiting: { label: 'Ожидает', dotClass: 'waiting' },
    inProgress: { label: 'В процессе', dotClass: 'inProgress' },
    canceled: { label: 'Отменен', dotClass: 'canceled' },
  };

  // Конфигурация колонок для таблицы
  const tableColumns: ColumnConfig[] = [
    {
      id: 'date',
      title: 'Дата',
      render: (row) => (
        <>
          <div className="table-cell-title">Дата</div>
          <div className="table-cell-value">{formatBackendShortDate(row.checkTime)}</div>
        </>
      ),
    },
    {
      id: 'num',
      title: 'Номер заявки',
      render: (row) => (
        <>
          <div className="table-cell-title">Номер заявки</div>
          <div className="table-cell-value">{row.num}</div>
        </>
      ),
    },
    {
      id: 'time',
      title: 'Время',
      render: (row) => (
        <>
          <div className="table-cell-title">Время</div>
          <div className="table-cell-value">{formatBackendTime(row.checkTime)}</div>
        </>
      ),
    },
    {
      id: 'medic',
      title: 'Медик',
      render: (row) => (
        <>
          <div className="table-cell-title">Медик</div>
          <div className="table-cell-value">{fullName(row.medic) || 'Не указан'} &nbsp; </div>
        </>
      ),
      className: 'table-cell-centered',
    },
    {
      id: 'status',
      title: 'Статус',
      render: (row) => {
        const status = row.status === 'completed' ? 'completed' : row.status === 'rejected' ? 'rejected' : 'waiting';
        return <StatusCell status={row.status} statusMap={statusMap} />;
      },
      className: 'table-cell-centered',
    },
    {
      id: 'actions',
      title: 'Смотреть',
      render: () => null, // Рендеринг происходит внутри компонента Table
    },
  ];

  // Конфигурация вторичных строк (всегда видимых)
  const secondaryRows: SecondaryRowConfig[] = [
    {
      id: 'autopark',
      condition: (row) => !!row.autopark,
      content: (row) => (
        <LabeledSecondaryCell
          title="Автопарк"
          value={row.autopark.tradeName || row.autopark.legalName || 'Не указан'}
          colSpan={6}
        />
      ),
    },
  ];

  // Конфигурация расширяемых строк
  const expandedRows: ExpandedRowConfig[] = [
    {
      id: 'parameters',
      content: (row) => (
        <>
          <LabeledCell title="Температура" value={row.bodyTemperature} />
          <LabeledCell title="Давление" value={row.bloodPressure} />
          <LabeledCell title="Пульс" value={row.heartRate} />
          <LabeledCell title="Насыщение O2" value={row.saturation || '-'} colSpan={3} />
        </>
      ),
    },
    {
      id: 'conclusion',
      content: (row) => (
        <>
          <LabeledCell title="Алкотестер" value={`${row.bloodAlcohol} pm`} />
          <LabeledCell title="Жалобы" value={row.symptomsPresent ? 'Есть' : 'Нет'} />
          <LabeledCell
            title="Заключение"
            value={row.status === 'completed' ? 'Допущен' : row.status === 'rejected' ? 'Не допущен' : 'Ожидает'}
            colSpan={3}
          />
        </>
      ),
    },
    {
      id: 'refusal',
      content: (row) =>
        row.reasonForRefusal ? (
          <td colSpan={6}>
            <div className="reason-section">
              <div className="reason-title">Причина отказа:</div>
              <div className="reason-text">{row.reasonForRefusal}</div>
            </div>
          </td>
        ) : null,
    },
    {
      id: 'photos',
      content: (row) =>
        row.selfie ? (
          <td colSpan={6}>
            <div className="photo-section">
              <div className="photo-title">Фото:</div>
              <img src={normalizeImageUrl(row.selfie)} alt="Фото водителя" className="driver-photo" />
            </div>
          </td>
        ) : null,
    },
  ];

  // Подсчет статистики медицинских осмотров
  const medStats = useMemo(() => {
    const totalCount = medLists?.length || 0;
    const positiveCount = medLists?.filter((check) => check.status === 'completed').length || 0;
    const negativeCount = medLists?.filter((check) => check.status === 'rejected').length || 0;

    return {
      totalCount,
      positiveCount,
      negativeCount,
    };
  }, [medLists]);

  // Загрузка данных с учетом фильтров
  useEffect(() => {
    async function fetchMedLists() {
      if (!driver?.id) return;

      setLoading(true);
      setError('');

      try {
        // Приводим фильтры к нужному типу MedListFilters перед передачей
        const apiFilters: MedListFilters = {
          page: filters.page,
          limit: filters.limit,
          startTime: filters.startTime,
          endTime: filters.endTime,
        };

        const response = await MedicalService.getDriverMedLists(driver.id, apiFilters);
        setMedLists(response.data || []);

        // Вычисляем количество страниц
        const totalPages = Math.ceil((response.totalCount || 0) / (filters.limit || 10));
        setPagination({
          currentPage: response.page || 1,
          totalPages: response.totalPages || totalPages || 1,
          totalCount: response.totalCount || 0,
        });
      } catch (error) {
        console.error('Ошибка при загрузке медицинских осмотров:', error);
        setError('Не удалось загрузить данные о медицинских осмотрах');

        // Сбрасываем данные при ошибке
        setMedLists([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
        });
      } finally {
        setLoading(false);
      }
    }

    fetchMedLists();
  }, [driver, filters]);

  if (loading) return <Spiner />;

  if (error) return <div className="errorMessage">{error}</div>;

  return (
    <div className="driverMedList">
      <div className="infoCard">
        {/* Блок фильтров */}
        <DateRangeFilter
          startTime={filters.startTime}
          endTime={filters.endTime}
          onFilterChange={handleFilterChange}
          onResetFilters={resetFilters}
          className="medFilters"
        />

        {/* Блок статистики медицинских осмотров */}
        <div className="medStatsContainer" style={{ padding: '10px 25px' }}>
          <div className="medStatsText" style={{ fontWeight: 'bold', color: '#333' }}>
            Осмотров: {medStats.totalCount}, из них Положительных {medStats.positiveCount}, Отрицательных{' '}
            {medStats.negativeCount}
          </div>
        </div>

        {loading ? (
          <div className="loadingIndicator">Загрузка...</div>
        ) : error ? (
          <div className="errorMessage">{error}</div>
        ) : (
          <>
            <div className="admListContainer">
              <Table
                data={medLists}
                columns={tableColumns}
                expandedRows={expandedRows}
                secondaryRows={secondaryRows}
                keyField="id"
                noDataMessage="Нет данных о медицинских осмотрах"
              />
            </div>

            {/* Пагинация */}
            {pagination.totalPages > 1 && (
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
